import { CartAddEvent } from '@theme/events';

/**
 * Vivier Gift Guide behaviour.
 *
 * - Add to bag: any form inside a [data-vgg-add] container is submitted with fetch and announced through
 *   the theme's CartAddEvent, so the cart drawer / cart bubble update like they do on product pages.
 * - <vgg-carousel>: keeps the progress bar of a scroll-snap carousel in sync.
 * - <vgg-filter>: filters program cards by skin concern (also reacts to ?filter= and concern chips).
 * - <vgg-nav>: highlights the jump-nav link of the section in view and tells the page how tall the nav is.
 * - <vgg-giftcard>: swaps the gift card amount and the call to action link.
 */

const ADDED_RESET_DELAY = 1600;
const FILTER_EVENT = 'vgg:filter';

/* ---------------------------------------------------------------------------------------------
 * Add to bag
 * ------------------------------------------------------------------------------------------- */

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const host = form.closest('[data-vgg-add]');
  if (!(host instanceof HTMLElement)) return;

  event.preventDefault();

  const button = form.querySelector('button[type="submit"]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const label = button.querySelector('[data-vgg-label]');
  const status = host.querySelector('[data-vgg-status]');
  const error = host.querySelector('[data-vgg-error]');
  const originalLabel = label?.textContent ?? '';

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (error instanceof HTMLElement) error.hidden = true;

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
    .filter(Boolean);
  if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Unable to add this item.');
    }

    const variantId = String(formData.get('id') ?? '');
    form.dispatchEvent(
      new CartAddEvent({}, variantId, {
        source: 'vivier-gift-guide',
        itemCount: Number(formData.get('quantity')) || 1,
        productId: host.dataset.productId,
        variantId,
        sections: data.sections,
      })
    );

    button.dataset.state = 'added';
    if (label) label.textContent = host.dataset.addedLabel || originalLabel;
    if (status) status.textContent = host.dataset.addedMessage || host.dataset.addedLabel || '';

    setTimeout(() => {
      delete button.dataset.state;
      if (label) label.textContent = originalLabel;
      if (status) status.textContent = '';
    }, ADDED_RESET_DELAY);
  } catch (caught) {
    if (error instanceof HTMLElement) {
      error.textContent = caught instanceof Error ? caught.message : String(caught);
      error.hidden = false;
    }
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});

/* ---------------------------------------------------------------------------------------------
 * Concern chips: ask <vgg-filter> to filter, then let the anchor scroll to the programs section
 * ------------------------------------------------------------------------------------------- */

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-vgg-apply-filter]') : null;
  if (!target) return;

  document.dispatchEvent(
    new CustomEvent(FILTER_EVENT, { detail: { key: target.getAttribute('data-vgg-apply-filter') || 'all' } })
  );
});

/* ---------------------------------------------------------------------------------------------
 * Carousel progress bar
 * ------------------------------------------------------------------------------------------- */

class VggCarousel extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const track = this.#track();
    track?.addEventListener('scroll', this.#update, { passive: true, signal });
    window.addEventListener('resize', this.#update, { passive: true, signal });

    this.querySelector('[data-vgg-prev]')?.addEventListener('click', () => this.#page(-1), { signal });
    this.querySelector('[data-vgg-next]')?.addEventListener('click', () => this.#page(1), { signal });

    // Cards can be hidden or shown by <vgg-filter>, which changes how far the track scrolls.
    if (track) {
      const observer = new MutationObserver(this.#update);
      observer.observe(track, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
      signal.addEventListener('abort', () => observer.disconnect());
    }

    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #track() {
    return this.querySelector('[data-vgg-track]');
  }

  /**
   * Scrolls the track by most of its visible width.
   * @param {number} direction - -1 for previous, 1 for next
   */
  #page(direction) {
    const track = this.#track();
    if (!(track instanceof HTMLElement)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #update = () => {
    const track = this.#track();
    const thumb = this.querySelector('[data-vgg-thumb]');
    if (!(track instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

    const visible = track.clientWidth / track.scrollWidth;
    const offset = Math.abs(track.scrollLeft) / track.scrollWidth;

    this.toggleAttribute('data-static', visible >= 0.999);
    thumb.style.insetInlineStart = `${offset * 100}%`;
    thumb.style.inlineSize = `${Math.min(visible, 1) * 100}%`;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const prev = this.querySelector('[data-vgg-prev]');
    const next = this.querySelector('[data-vgg-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = Math.abs(track.scrollLeft) <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = Math.abs(track.scrollLeft) >= maxScroll - 1;
  };
}

/* ---------------------------------------------------------------------------------------------
 * Program filter
 * ------------------------------------------------------------------------------------------- */

class VggFilter extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const pill of this.querySelectorAll('[data-vgg-pill]')) {
      pill.addEventListener('click', () => this.apply(pill.getAttribute('data-vgg-pill') || 'all'), { signal });
    }

    document.addEventListener(
      FILTER_EVENT,
      (event) => {
        if (event instanceof CustomEvent) this.apply(event.detail?.key || 'all');
      },
      { signal }
    );

    const fromUrl = new URL(window.location.href).searchParams.get('filter');
    if (fromUrl) this.apply(fromUrl);
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * @param {string} key - A filter key, or "all"
   */
  apply(key) {
    const pills = [...this.querySelectorAll('[data-vgg-pill]')];
    const cards = [...this.querySelectorAll('[data-vgg-category]')];

    // Ignore keys that no program uses, so the grid never ends up empty.
    const known = key === 'all' || cards.some((card) => card.getAttribute('data-vgg-category') === key);
    const active = known ? key : 'all';

    for (const pill of pills) {
      pill.setAttribute('aria-pressed', String(pill.getAttribute('data-vgg-pill') === active));
    }

    for (const card of cards) {
      card.hidden = active !== 'all' && card.getAttribute('data-vgg-category') !== active;
    }
  }
}

/* ---------------------------------------------------------------------------------------------
 * Sticky jump nav
 * ------------------------------------------------------------------------------------------- */

class VggNav extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;
  #frame = 0;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    window.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    document.documentElement.style.removeProperty('--vgg-scroll-offset');
  }

  #schedule = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const stuckAt = this.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--vgg-scroll-offset', `${Math.ceil(stuckAt) + 16}px`);

    const links = [...this.querySelectorAll('a[href^="#"]')];
    let current = null;

    for (const link of links) {
      const id = decodeURIComponent(link.hash.slice(1));
      const section = id ? document.getElementById(id) : null;
      if (section && section.getBoundingClientRect().top <= stuckAt + 40) current = link;
    }

    for (const link of links) {
      if (link === current) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }

    const scroller = this.querySelector('[data-vgg-nav-scroller]');
    if (current instanceof HTMLElement && scroller instanceof HTMLElement) {
      const target = current.offsetLeft - (scroller.clientWidth - current.offsetWidth) / 2;
      scroller.scrollTo({ left: target, behavior: 'auto' });
    }
  };
}

/* ---------------------------------------------------------------------------------------------
 * Gift card amount picker
 * ------------------------------------------------------------------------------------------- */

class VggGiftcard extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const amount of this.querySelectorAll('[data-vgg-amount]')) {
      amount.addEventListener('click', () => this.#select(amount), { signal });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * @param {Element} chosen
   */
  #select(chosen) {
    for (const amount of this.querySelectorAll('[data-vgg-amount]')) {
      amount.setAttribute('aria-pressed', String(amount === chosen));
    }

    const cta = this.querySelector('[data-vgg-cta]');
    if (!(cta instanceof HTMLAnchorElement)) return;

    const url = chosen.getAttribute('data-url');
    if (url) cta.href = url;

    const template = cta.getAttribute('data-template') ?? '';
    cta.textContent = template.replace('{amount}', chosen.getAttribute('data-amount') ?? '');
  }
}

for (const [tag, constructor] of [
  ['vgg-carousel', VggCarousel],
  ['vgg-filter', VggFilter],
  ['vgg-nav', VggNav],
  ['vgg-giftcard', VggGiftcard],
]) {
  if (!customElements.get(tag)) customElements.define(tag, constructor);
}
